import type { OrderLineItemMostRecent } from "~~/models/documents";
import type { CategoryFilter, FilterGroupMultiSelect } from "~~/models/filters";
import type { ProductListItem, ProductOverpackItem } from "~~/models/products";
import type { ProductSearchDto, PropertyFilterDto } from "~~/server/api/ecom/products/search.post";
import type {
  PlanktonCategoryWithSlugs,
  PlanktonProductSearchCategoryFilterResponse,
  PlanktonProductSearchIdResponse,
  PlanktonProductSearchResponse,
} from "~~/server/plankton";
import { useDebounceFn } from "@vueuse/core";
import { z } from "zod";
import { CustomSearchResultSortOrder } from "~~/models/custom-search-result-sort-order";
import { PlanktonSearchResultSortOrder } from "~~/server/plankton";
import { NatchOSPlanktonImplementationModelsDocumentType } from "~~/server/plankton/schema";
import { chunkNumbers } from "~~/server/utils/chunking";
import { mapToPropertyFilters, ThirteenMonthsInDays } from "~/utils/filters";

export function useCatalog(
  options: {
    initialize: {
      searchState: boolean;
      catalog: boolean;
      ssr: boolean; // Whether to execute the catalog fetch on the server side during SSR (false when logged in, to include user context)
      scrollIntoViewThreshold: number; // Scroll into view only when the scroll Y position is above this threshold
    };
    pageTitle?: string | null; // Active page title, if not set, it will be derived from the search term or category name
    params?: {
      category?: PlanktonCategoryWithSlugs | null;
      productIdList?: Array<number>;
      propertyFilterList?: Array<PropertyFilterDto>; // Pre-filter search without returning in active filters
      pageSize?: {
        value?: number;
        modifier?: number; // Modifier to adjust the page size, e.g. for product group promos
      };
    };
    attach?: {
      catalogAsFallback?: boolean;
      orderInfo?: boolean;
    };
    seo?: {
      listName?: "Orders" | "Results" | "Templates" | null;
    };
  } = { initialize: { searchState: false, catalog: false, ssr: false, scrollIntoViewThreshold: 2000 } },
) {
  const initPageSize = (options.params?.pageSize?.value ?? defaultCatalogPageSize) + (options.params?.pageSize?.modifier ?? 0);

  const sortOrderOptions = options.seo?.listName === "Templates" || options.seo?.listName === "Orders"
    ? [
        CustomSearchResultSortOrder.MainCategory, // "Hoofdcategorie"
        PlanktonSearchResultSortOrder.PersonalizedThenProductNumberAscending, // = ABC-sortering, "Standaard"/"Défaut" in ML labels
        PlanktonSearchResultSortOrder.ProductNameAscending,
        PlanktonSearchResultSortOrder.ProductNameDescending,
      ]
    : [
        PlanktonSearchResultSortOrder.PersonalizedThenProductNumberAscending, // = ABC-sortering, "Standaard"/"Défaut" in ML labels
        PlanktonSearchResultSortOrder.ProductNameAscending,
        PlanktonSearchResultSortOrder.ProductNameDescending,
      ];

  const initSortOrder = sortOrderOptions.at(0);

  const { locale, t } = useI18n();
  const { path } = useRoute();
  const { trackProductList, trackSearch } = useGtmTracking();

  // below states need to be global states so every instance of this composable works with the same values
  const catalogPaged = useCatalogPaged(path);
  const catalogDisplay = useCatalogDisplay(path);
  const catalogPagination = useCatalogPagination(path);
  const catalogSearchState = useCatalogSearchState(path);

  const querySchema = z.object({
    term: z.string().optional(),
    filters: z.string().optional(),
    sortOrder: z.coerce.number().optional(),
    lastDays: z.coerce.number().optional(),
    page: z.coerce.number().optional().default(defaultCatalogPage),
    pageSize: z.coerce.number().optional().default(initPageSize),
  });

  const { query } = useRoute();
  const { push } = useRouter();

  const { success, data: validQueryData } = querySchema.safeParse(query);

  if (options.initialize.searchState && success) {
    catalogSearchState.value.term = validQueryData?.term;

    if (validQueryData?.term?.length) {
      trackSearch(validQueryData.term);
    }

    catalogSearchState.value.sortOrderOptions = sortOrderOptions;
    catalogSearchState.value.sortOrder = validQueryData?.sortOrder ?? Number(initSortOrder);

    catalogSearchState.value.page = validQueryData?.page;
    catalogSearchState.value.pageSize = validQueryData?.pageSize ?? initPageSize;
    catalogSearchState.value.productIdList = options.params?.productIdList;

    if (options.seo?.listName === "Orders") {
      catalogSearchState.value.lastDays = validQueryData?.lastDays ?? ThirteenMonthsInDays;
    }
  }

  const activeTitle = computed(() => {
    if (options.pageTitle) {
      return options.pageTitle;
    }

    if (catalogSearchState.value.term) {
      return `${t("catalog.search-by")} '${catalogSearchState.value.term}'`;
    }

    return options.params?.category?.translations[locale.value.substring(0, 2)]?.name || null;
  });

  const activeFilters = computed((): Array<PropertyFilterDto> => {
    if (catalogSearchState.value.propertyFilters.length) {
      return catalogSearchState.value.propertyFilters
        .flatMap((pg: FilterGroupMultiSelect) => pg.selectedList
          ?.filter((s: number | null | undefined) => s !== null && s !== undefined)
          .map((s: number) => {
            return { propertyId: pg.id, propertyValueId: s } satisfies PropertyFilterDto;
          }))
        ?.filter((pf: PropertyFilterDto | null | undefined) => pf !== null && pf !== undefined);
    }

    if (options.initialize.searchState) {
      return parsePropertyFilters(validQueryData?.filters ?? "") ?? [];
    }

    return [];
  });

  const getFullCatalog = async (options: { attachOrderInfo: boolean }) => {
    const productIdList: Array<number> = [];

    const catalogPagedKeys = Object.keys(catalogPaged.value).map(Number);

    for (const catalogPagedKey of catalogPagedKeys) {
      const pagedProductIdList = catalogPaged.value[catalogPagedKey]
        ?.filter((p: any) => p.productId)
        .flatMap((p: any) => p.productId!) || [];

      productIdList.push(...pagedProductIdList);
    }

    if (!productIdList.length) {
      return [];
    }

    // splitting in chunks as URL length might be exceeded
    const productIdChunks = chunkNumbers(200, productIdList);

    const batchPromises = productIdChunks.map((chunk: Array<number>) =>
      $fetch<Array<ProductListItem>>("/api/ecom/products/list", {
        query: {
          productIdList: chunk,
          addOverpacks: true,
          addOverpackTranslations: true,
          addProductTranslations: true,
          locale: locale.value, // Use the current locale (the detector would fallback to the default locale at SSR)
        },
      }));

    const batchResults = await Promise.all(batchPromises);
    const products: Array<ProductListItem> = batchResults.flat();

    if (options.attachOrderInfo) {
      const batchPromises = productIdChunks.map((chunk: Array<number>) =>
        $fetch<Array<OrderLineItemMostRecent>>("/api/ecom/documents/most-recent", {
          query: {
            productIdList: chunk,
            documentTypeList: [NatchOSPlanktonImplementationModelsDocumentType.Order],
            locale: locale.value, // Use the current locale (the detector would fallback to the default locale at SSR)
          },
        }));

      const batchResults = await Promise.all(batchPromises);
      const mostRecentDocumentLines: Array<OrderLineItemMostRecent> = batchResults.flat();

      for (const product of products) {
        const mostRecentDocumentLine = mostRecentDocumentLines.find((line: OrderLineItemMostRecent) => line.productId === product.id);

        if (!mostRecentDocumentLine) {
          continue;
        }

        product.orderQuantity = 0; // no need to copy historic order quantity

        const orderOverpack = product.overpacks?.find((overpack: ProductOverpackItem) => overpack.code === mostRecentDocumentLine.orderOverpackCode);
        product.orderOverpackId = orderOverpack?.value
          ?? product.defaultOverpack?.value
          ?? product.overpacks?.at(0)?.value
          ?? null;
      }
    }

    return products;
  };

  const setDisplayCatalog = async () => {
    if (!catalogPaged.value) {
      catalogDisplay.value = [];
      return;
    }

    const currentPage = catalogSearchState.value.page ?? defaultCatalogPage;
    const catalog = catalogPaged.value[currentPage] as Array<PlanktonProductSearchIdResponse>;

    if (!catalog) {
      catalogDisplay.value = [];
      return;
    }

    const productIdList = catalog
      .sort((a: PlanktonProductSearchIdResponse, b: PlanktonProductSearchIdResponse) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((p: PlanktonProductSearchIdResponse) => p.productId);

    const products = await $fetch<Array<ProductListItem>>("/api/ecom/products/list", {
      query: {
        productIdList: productIdList,
        addCategories: true,
        addExternalData: true,
        addFiles: true,
        addOverpacks: true,
        addOverpackTranslations: true,
        addProductTranslations: true,
        addPropertyValues: true,
        addStoreData: true,
        locale: locale.value, // Use the current locale (the detector would fallback to the default locale at SSR)
      },
    });

    catalogDisplay.value = products;

    if (options.attach?.orderInfo) {
      const mostRecentDocumentLines = await $fetch<Array<OrderLineItemMostRecent>>("/api/ecom/documents/most-recent", {
        query: {
          productIdList: productIdList,
          documentTypeList: [NatchOSPlanktonImplementationModelsDocumentType.Order],
          locale: locale.value, // Use the current locale (the detector would fallback to the default locale at SSR)
        },
      });

      for (const product of catalogDisplay.value) {
        const mostRecentDocumentLine = mostRecentDocumentLines.find((line: OrderLineItemMostRecent) => line.productId === product.id);

        if (!mostRecentDocumentLine) {
          continue;
        }

        product.orderQuantity = 0; // no need to copy historic order quantity

        const orderOverpack = product.overpacks?.find((overpack: ProductOverpackItem) => overpack.code === mostRecentDocumentLine.orderOverpackCode);
        product.orderOverpackId = orderOverpack?.value
          ?? product.defaultOverpack?.value
          ?? product.overpacks?.at(0)?.value
          ?? null;
      }
    }

    if (catalogSearchState.value.productIdList && catalogSearchState.value.productIdList.length > 0) {
      trackProductList(catalogDisplay.value.filter(c => c).map(c => c.dataLayer), options.seo?.listName ?? "Results");
    }
  };

  const updateHistory = () => {
    push({
      query: {
        term: !catalogSearchState.value.term || !catalogSearchState.value.term.length ? undefined : catalogSearchState.value.term,
        filters: !activeFilters.value.length ? undefined : JSON.stringify(activeFilters.value),
        sortOrder: catalogSearchState.value.sortOrder === Number(initSortOrder) ? undefined : catalogSearchState.value.sortOrder,
        lastDays: catalogSearchState.value.lastDays ?? undefined,
        page: catalogSearchState.value.page === defaultCatalogPage ? undefined : catalogSearchState.value.page,
        pageSize: catalogSearchState.value.pageSize === initPageSize ? undefined : catalogSearchState.value.pageSize,
      },
    });
  };

  const { status, execute, error } = useFetch("/api/ecom/products/search", {
    server: options.initialize.ssr,
    method: "POST",
    lazy: true,
    immediate: options.initialize.catalog,
    dedupe: "defer",
    body: computed(() =>
      ({
        searchTerm: !catalogSearchState.value.term || !catalogSearchState.value.term.length ? undefined : catalogSearchState.value.term,
        categoryId: options.params?.category?.id ?? 0,
        productIdFilterList: catalogSearchState.value.productIdList,
        productsBoughtByCustomerMaximumAgeInDays: catalogSearchState.value.lastDays ?? undefined,
        propertyValueFilterList: [...activeFilters.value, ...options.params?.propertyFilterList ?? []],
        sortOrder: catalogSearchState.value.sortOrder,
        pageSize: catalogSearchState.value.pageSize,
        catalogAsFallback: options.attach?.catalogAsFallback ?? false,
        locale: locale.value, // For reload purposes after language switching
      } as ProductSearchDto),
    ),
    watch: false,
    deep: false,
    onRequest: ({ request, options }) => {
      console.info(`[Catalog] Search request (${JSON.stringify(request)} | body: ${JSON.stringify(options.body)})`);
    },
    onResponse: async ({ response }) => {
      const responseData = response._data as { isFallback: boolean; results?: PlanktonProductSearchResponse | undefined };
      // console.info("[Catalog] Search response", responseData);

      if (!responseData.results) {
        return;
      }

      catalogPagination.value.pageSize = responseData.results.pageSize ?? 0;
      catalogPagination.value.pageSizeModifier = options.params?.pageSize?.modifier ?? 0;
      catalogPagination.value.totalItems = responseData.results.resultCount ?? 0;

      catalogPaged.value = responseData.results.pagedProducts;
      await setDisplayCatalog();

      catalogSearchState.value.propertyFilters = await mapToPropertyFilters(
        responseData.results,
        activeFilters.value,
        locale.value,
      );

      // Filter out pre-filters from the property filters
      catalogSearchState.value.propertyFilters = catalogSearchState.value.propertyFilters.filter((pf: FilterGroupMultiSelect) =>
        !options.params?.propertyFilterList?.some(
          (f: PropertyFilterDto) => f.propertyId === pf.id,
        ),
      );

      catalogSearchState.value.categoryFilters = responseData.results.categoryFilters
        ?.filter((cf: PlanktonProductSearchCategoryFilterResponse) => cf.hasOnlineProducts)
        .map((cf: PlanktonProductSearchCategoryFilterResponse) => {
          return { categoryId: cf.categoryId, productCount: cf.onlineProductCountSubtree ?? 0 } satisfies CategoryFilter;
        });

      catalogSearchState.value.isFallback = responseData.isFallback;
    },
    onResponseError: (error) => {
      console.error("Could not get catalog data", error.response);
    },
  });

  if (options.initialize.searchState) {
    watch(() => catalogSearchState.value.lastDays, async (newLastDays, oldLastDays) => {
      if (!newLastDays || newLastDays === oldLastDays) {
        return;
      }

      await execute(); // refresh catalog

      if (catalogSearchState.value.page === defaultCatalogPage) {
        updateHistory();
      }
      else {
        catalogSearchState.value.page = defaultCatalogPage;
      }
    });

    watch(() => catalogSearchState.value.page, async (_newPage, _oldPage) => {
      // do not scroll back to top when the page is already in view
      if (window.scrollY > options.initialize.scrollIntoViewThreshold) {
        const scrollTarget = document?.querySelector("#scroll-into-view");
        scrollTarget?.scrollIntoView({ behavior: "smooth" });
      }

      updateHistory();
      await setDisplayCatalog();
    });

    watch(() => catalogSearchState.value.pageSize, async (newPageSize, oldPageSize) => {
      if (!newPageSize || newPageSize === oldPageSize) {
        return;
      }

      await execute(); // refresh catalog

      if (catalogSearchState.value.page === defaultCatalogPage) {
        updateHistory();

        const overview = document?.querySelector("#scroll-into-view");
        overview?.scrollIntoView({ behavior: "smooth" });
      }
      else {
        catalogSearchState.value.page = defaultCatalogPage;
      }
    });

    // when using a deep watcher, create a computed property and watch that instead, otherwise, oldValue is always the same as newValue
    const watchedPropertyFilters = computed(() => structuredClone(toRaw(catalogSearchState.value.propertyFilters
      .flatMap((pf: FilterGroupMultiSelect) => (pf.selectedList ?? []).sort()))));

    const debouncedSearchData = useDebounceFn(execute, 500, { maxWait: 5000 });

    watch(watchedPropertyFilters, async (newPropertyFilters, oldPropertyFilters) => {
      if (!newPropertyFilters || JSON.stringify(newPropertyFilters) === JSON.stringify(oldPropertyFilters)) {
        return;
      }

      debouncedSearchData(); // refresh catalog

      if (catalogSearchState.value.page === defaultCatalogPage) {
        updateHistory();
      }
      else {
        catalogSearchState.value.page = defaultCatalogPage;
      }
    }, { deep: true });

    watch(() => catalogSearchState.value.sortOrder, async (newSortOrder, oldSortOrder) => {
      if (!newSortOrder || newSortOrder === oldSortOrder) {
        return;
      }

      await execute(); // refresh catalog

      if (catalogSearchState.value.page === defaultCatalogPage) {
        updateHistory();
      }
      else {
        catalogSearchState.value.page = defaultCatalogPage;
      }
    });
  }

  return {
    status: status,
    error: error,
    catalog: catalogDisplay,
    catalogPaged: readonly(catalogPaged),
    searchState: catalogSearchState,
    activeTitle: activeTitle,
    activeFilters: activeFilters,
    pagination: catalogPagination,
    getFullCatalog: getFullCatalog,
    updateHistory: updateHistory,
    execute: execute,
  };
}
